import { useUser } from "@clerk/clerk-react";
import { useEffect, useState, useRef, useCallback } from "react";
import debounce from "lodash.debounce";
import moment from "moment";
import { BlockBlobClient } from "@azure/storage-blob";
import { getBlobSasToken } from "../api/api-methods";

const DEBOUNCE_DELAY = 200;
const ROLE_DEBOUNCE_DELAY = 250; // Debounce role changes by 250ms
const LAST_KNOWN_ROLE_KEY = "lastKnownRole";

export const useDebouncedEffect = (callback, dependencies, delay = 500) => {
  useEffect(() => {
    const debouncedCallback = debounce(callback, delay);
    debouncedCallback();

    return () => {
      debouncedCallback.cancel();
    };
  }, dependencies);
};

/**
 * Enhanced useUserRole hook with debouncing and fallback
 *
 * Features:
 * - Debounces role changes by 250ms to prevent UI flicker during role switch
 * - Falls back to localStorage "lastKnownRole" when role is undefined/null
 * - Persists valid roles to localStorage for instant fallback
 * - Returns stableRole instead of raw role to prevent blank screens
 *
 * @returns {string|null} The stable, debounced user role
 */
export const useUserRole = () => {
  const { user } = useUser();

  // Get initial role from localStorage as fallback
  const getInitialRole = () => {
    const lastKnown = localStorage.getItem(LAST_KNOWN_ROLE_KEY);
    return lastKnown || null;
  };

  const [stableRole, setStableRole] = useState(getInitialRole);
  const pendingRoleRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Debounced role updater - prevents UI flicker during role switch
  const updateRole = useCallback((newRole) => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    pendingRoleRef.current = newRole;

    debounceTimerRef.current = setTimeout(() => {
      if (pendingRoleRef.current) {
        setStableRole(pendingRoleRef.current);
        // Persist valid roles to localStorage for fallback
        localStorage.setItem(LAST_KNOWN_ROLE_KEY, pendingRoleRef.current);
      }
    }, ROLE_DEBOUNCE_DELAY);
  }, []);

  useEffect(() => {
    const rawRole = user?.publicMetadata?.role;

    if (rawRole && rawRole !== "Unknown Role") {
      // Valid role received - debounce the update
      updateRole(rawRole);
    } else if (!rawRole && !stableRole) {
      // No role and no stable role - try localStorage fallback
      const lastKnown = localStorage.getItem(LAST_KNOWN_ROLE_KEY);
      if (lastKnown) {
        setStableRole(lastKnown);
      }
    }
    // If rawRole is undefined but we have stableRole, keep showing stableRole
    // This prevents blank screen during Clerk metadata refresh

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [user, updateRole, stableRole]);

  return stableRole;
};

/**
 * Hook to get both raw and stable role (useful for debugging)
 */
export const useUserRoleDebug = () => {
  const { user } = useUser();
  const stableRole = useUserRole();
  const rawRole = user?.publicMetadata?.role;

  return {
    rawRole,
    stableRole,
    isStale: rawRole !== stableRole,
    lastKnown: localStorage.getItem(LAST_KNOWN_ROLE_KEY),
  };
};

export const dateFormat = (date) => {
  const formatted = moment.utc(date).format("MMMM D, YYYY");
  return formatted;
};

export const calculateDaysAgo = (dateString) => {
  const daysAgo = moment().diff(moment(dateString), "days");

  if (daysAgo === 0) return "Today";
  return `${daysAgo}d ago`;
};

export const uploadFile = async (file, guid, entityType) => {
  const selectedFile = file;

  if (!selectedFile) {
    return;
  }

  try {
    // Fetch the SAS token and other required data
    const blobData = await getBlobSasToken(entityType, guid);

    const { storage_account_name, container_name, blob_path, sas_token } =
      blobData;

    // Validate blobData to check if required properties exist
    if (!storage_account_name || !container_name || !sas_token) {
      console.error("Missing required blob data:", blobData);
      return;
    }

    const blobUrl = `https://${storage_account_name}.blob.core.windows.net/${container_name}/${selectedFile.name}?${sas_token}`;

    const blobClient = new BlockBlobClient(blobUrl);

    // Upload the file
    await blobClient.uploadData(selectedFile);

    return blobUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    alert("An error occurred during upload. Please try again.");
    return null; // Ensure the function always returns something, even in case of error
  }
};

// export const getCityStateCountry = async (address) => {
//   const API_KEY = "process.env.REACT_APP_GOOGLE_MAPS_API_KEY";
//   if (!API_KEY) {
//     console.error("Google Maps API key is missing!");
//     return null;
//   }

//   const encodedAddress = encodeURIComponent(address);
//   const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${API_KEY}`;

//   try {
//     const response = await fetch(url);
//     const data = await response.json();

//     if (data.status !== "OK" || !data.results.length) {
//       console.error("Geocoding failed:", data.status);
//       return null;
//     }

//     const addressComponents = data.results[0].address_components;

//     let city = "";
//     let state = "";
//     let country = "";

//     addressComponents.forEach((component) => {
//       if (
//         component.types.includes("locality") ||
//         component.types.includes("administrative_area_level_2")
//       ) {
//         city = component.long_name;
//       } else if (component.types.includes("administrative_area_level_1")) {
//         state = component.long_name;
//       } else if (component.types.includes("country")) {
//         country = component.long_name;
//       }
//     });

//     return { city, state, country };
//   } catch (error) {
//     console.error("Error fetching location data:", error);
//     return null;
//   }
// };

export const formatUserDetails = (details) => {
  if (!details) return null;
  return {
    id: details.id,
    name: details.full_name,
    email: details.email,
    phone: details.phone_number || details.office_phone_number,
    first_Name: details.first_name,
    last_Name: details.last_name,
    created_Date: details.create_date_time,
    updated_Date: details.update_date_time,
    status: details.is_active ? "Active" : "Inactive",
    sponsorId: details.sponsor_id,
    contact_Preference: details.contact_preference,
  };
};
const today = new Date();
export const twoWeeks = new Date();
twoWeeks.setDate(today.getDate() + 14);

export const fortyFiveDays = new Date();
fortyFiveDays.setDate(today.getDate() + 45);

export const dateFormatRFi = (date) => {
  return moment.utc(date).format("MMM YYYY");
};

export const userHasRight = (rights, rightName) => {
  return rights.includes(rightName);
};

/**
 * Returns true if the user is considered an admin (view-only: can see Discovery, Doc Library, Assessment Calendar but Request capabilities are hidden).
 * Includes: Sponsor Admin, Backend User, Backend Master User.
 * @param {string|null} userRole - Role from useUserRole() (e.g. "Sponsor User", "Backend Master User").
 * @param {string[]} subRoles - Array from user?.publicMetadata?.sub_role.
 * @returns {boolean}
 */
export const isAdminUser = (userRole, subRoles) => {
  if (userRole === "Backend User" || userRole === "Backend Master User") return true;
  if (userRole === "Sponsor Admin") return true;
  const roles = Array.isArray(subRoles) ? subRoles : [];
  return roles.some((r) => r === "Sponsor Admin" || (r && r.toLowerCase() === "sponsor admin"));
};

/**
 * Returns true only for Backend User and Backend Master User.
 * Use this when hiding Request capabilities (view-only): only these roles should have Document Library / Assessment Calendar / Discovery request actions hidden.
 * Sponsor Admin must remain able to make requests.
 */
export const isBackendViewOnlyUser = (userRole) => {
  return userRole === "Backend User" || userRole === "Backend Master User";
};

/**
 * Diligent Admin roles that can archive requests and see archive metadata (matches backend Authorize).
 */
export const isDiligentAdminUserRole = (userRole) =>
  userRole === "Backend User" || userRole === "Backend Master User";

/**
 * Returns the type of user based on the user role and sub roles.
 * @param {string} userRole - The user role from useUserRole() (e.g. "Sponsor User", "Backend Master User").
 * @param {string[]} subRoles - Array from user?.publicMetadata?.sub_role.
 * @returns {string} The type of user (e.g. "admin", "sponsor", "provider", "unknown").
 */
export const getUserType = (userRole, subRoles) => {
  if (userRole === "Backend User" || userRole === "Backend Master User") return "admin";
  if (userRole === "Sponsor User" || userRole === "Sponsor Master User") return "sponsor";
  if (userRole === "Provider User" || userRole === "Provider Master User") return "vendor";
  return "unknown";
};